import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, App, Space, Progress, Alert } from 'antd';
import { LockOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/api';
import { SERVICE_TITLE } from '@shared/config/service-config';

const { Title, Text } = Typography;

interface SetupForm {
  password: string;
  confirmPassword: string;
}

interface PasswordStrength {
  score: number; // 0-100
  hasMinLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
}

function checkPasswordStrength(password: string): PasswordStrength {
  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);

  let score = 0;
  if (hasMinLength) score += 25;
  if (hasUpperCase) score += 25;
  if (hasLowerCase) score += 25;
  if (hasNumber) score += 25;

  return {
    score,
    hasMinLength,
    hasUpperCase,
    hasLowerCase,
    hasNumber,
  };
}

export function AgencySetupPage() {
  const { message: messageApi } = App.useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [userInfo, setUserInfo] = useState<{ email: string; name: string } | null>(null);
  const [password, setPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
  });
  const [form] = Form.useForm();

  // エラー表示済みフラグ（無限ループ防止）
  const errorShownRef = useRef(false);
  const noTokenErrorShownRef = useRef(false);

  // トークン検証
  const validateTokenQuery = trpc.agencyInvitation.validate.useQuery(
    { token: token || '' },
    {
      enabled: !!token,
      retry: false,
    }
  );

  // トークン検証結果の監視
  useEffect(() => {
    if (validateTokenQuery.isSuccess && validateTokenQuery.data) {
      setUserInfo({ email: validateTokenQuery.data.email, name: validateTokenQuery.data.name });
      setValidating(false);
    }
    if (validateTokenQuery.isError && !errorShownRef.current) {
      errorShownRef.current = true;
      messageApi.error(validateTokenQuery.error.message || 'トークンの検証に失敗しました');
      setValidating(false);
    }
  }, [validateTokenQuery.isSuccess, validateTokenQuery.isError, validateTokenQuery.data, validateTokenQuery.error, messageApi]);

  const isTokenValidationPending = validating || validateTokenQuery.isLoading;

  // パスワード設定
  const setupMutation = trpc.agencyInvitation.completeSetup.useMutation({
    onSuccess: () => {
      messageApi.success('パスワードの設定が完了しました');
      setLoading(false);
      setTimeout(() => {
        navigate('/agency/login', { replace: true });
      }, 2000);
    },
    onError: (error) => {
      messageApi.error(error.message || 'パスワードの設定に失敗しました');
      setLoading(false);
    },
  });

  useEffect(() => {
    if (!token && !noTokenErrorShownRef.current) {
      noTokenErrorShownRef.current = true;
      messageApi.error('無効なリンクです');
      setValidating(false);
    }
  }, [token, messageApi]);

  useEffect(() => {
    setPasswordStrength(checkPasswordStrength(password));
  }, [password]);

  const handleSubmit = async (values: SetupForm) => {
    if (!token) {
      messageApi.error('無効なトークンです');
      return;
    }

    if (values.password !== values.confirmPassword) {
      messageApi.error('パスワードが一致しません');
      return;
    }

    if (passwordStrength.score < 100) {
      messageApi.error('パスワードがすべての要件を満たしていません');
      return;
    }

    setLoading(true);
    setupMutation.mutate({
      token,
      password: values.password,
      confirmPassword: values.confirmPassword,
    });
  };

  const getProgressColor = () => {
    if (passwordStrength.score >= 75) return '#52c41a';
    if (passwordStrength.score >= 50) return '#faad14';
    return '#ff4d4f';
  };

  if (isTokenValidationPending) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Card style={{ maxWidth: '400px' }}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Title level={4}>招待リンクを確認中...</Title>
          </div>
        </Card>
      </div>
    );
  }

  if (!token || !userInfo) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Card style={{ maxWidth: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={4}>無効な招待リンクです</Title>
            <Text type="secondary">
              招待リンクの有効期限が切れているか、無効です。
            </Text>
            <div style={{ marginTop: '20px' }}>
              <Link to="/agency/login">
                <Button type="primary">ログインページへ</Button>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: '500px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={2} style={{ marginBottom: '8px' }}>
              代理店アカウント初期設定
            </Title>
            <Text type="secondary">{SERVICE_TITLE}</Text>
          </div>

          <Alert
            message="メール認証完了"
            description={`${userInfo.name} 様（${userInfo.email}）のメールアドレスが確認されました。初回パスワードを設定してください。`}
            type="success"
            showIcon
          />

          <Form
            form={form}
            name="agencySetup"
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'パスワードを入力してください' },
                { min: 8, message: 'パスワードは8文字以上で入力してください' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="新しいパスワード"
                autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Item>

            {password && (
              <div style={{ marginBottom: '24px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  パスワード強度:
                </Text>
                <Progress
                  percent={passwordStrength.score}
                  strokeColor={getProgressColor()}
                  showInfo={false}
                  size="small"
                  style={{ marginBottom: '8px' }}
                />
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text style={{ fontSize: '12px' }}>
                    {passwordStrength.hasMinLength ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    )}{' '}
                    8文字以上
                  </Text>
                  <Text style={{ fontSize: '12px' }}>
                    {passwordStrength.hasUpperCase ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    )}{' '}
                    大文字を含む
                  </Text>
                  <Text style={{ fontSize: '12px' }}>
                    {passwordStrength.hasLowerCase ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    )}{' '}
                    小文字を含む
                  </Text>
                  <Text style={{ fontSize: '12px' }}>
                    {passwordStrength.hasNumber ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    )}{' '}
                    数字を含む
                  </Text>
                </Space>
              </div>
            )}

            <Form.Item
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: '確認用パスワードを入力してください' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('パスワードが一致しません'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="パスワード（確認）"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                disabled={passwordStrength.score < 100}
              >
                パスワードを設定してログイン
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Link to="/agency/login">
              <Text type="secondary">ログインページに戻る</Text>
            </Link>
          </div>
        </Space>
      </Card>
    </div>
  );
}
